import React, { PureComponent, Fragment } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';

import { debounce } from 'packages';
import { toSearchString } from 'tradera-utils/url';
import { withIsMobile } from 'tradera-hooks/use-is-mobile';
import { STORAGE_KEYS, STORAGE_VALUES } from 'tradera-constants/storage';
import { localStore } from 'utils/storage';
import { withTranslator } from 'tradera-lang/translate';
import * as gtm from 'tradera-apps/tradera/components/discover/helpers/search-gtm';
import saveSearchApi from './save-search-button-api';

import BookmarkFillIcon from '@tradera/blueprint/theme/icons2/bookmark-fill.svg?jsx';
import BookmarkIcon from '@tradera/blueprint/theme/icons2/bookmark.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import InfoIcon from '@tradera/blueprint/theme/icons/info.svg?jsx';
import './save-search-button.scss';

const GA_CATEGORY = 'Saved Search';

class SaveSearchButton extends PureComponent {
    static propTypes = {
        queryParams: PropTypes.object.isRequired,
        searchName: PropTypes.string,
        categoryId: PropTypes.number,
        isSavedSearchEmailEnabled: PropTypes.bool
    };

    constructor(props) {
        super(props);
        this.state = {
            showSavedTooltip: false,
            showHintTooltip:
                localStore &&
                localStore.getItem(STORAGE_KEYS.SAVE_SEARCH_HINT) !==
                    STORAGE_VALUES.SAVE_SEARCH_HINT_CLICKED,
            categoryId: props.queryParams.categoryId,
            emailNotificationEnabled: false,
            savedSearchId: 0,
            inProgress: false
        };
        const debounceTimeout = 600;
        this.debouncedSaveSearch = debounce(
            this.debouncedSaveSearch,
            debounceTimeout
        );
        this.debouncedRemoveSaved = debounce(
            this.debouncedRemoveSaved,
            debounceTimeout
        );
        this.debouncedNotificationChange = debounce(
            this.debouncedNotificationChange,
            debounceTimeout
        );
    }

    componentDidUpdate(prevProps) {
        if (prevProps.queryParams !== this.props.queryParams) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ savedSearchId: 0 });
        }
    }

    componentDidMount() {
        const { queryParams } = this.props;
        if (queryParams.saveSearch) {
            this.saveSearch(queryParams.q);
        }
    }

    handleCloseTooltip = () => {
        localStore.setItem(
            STORAGE_KEYS.SAVE_SEARCH_HINT,
            STORAGE_VALUES.SAVE_SEARCH_HINT_CLICKED
        );
        this.setState({ showHintTooltip: false });
    };

    handleCloseSavedTooltip = () => {
        this.setState({ showSavedTooltip: false });
    };

    async toggleEmailNotificationEnabled() {
        const { savedSearchId, emailNotificationEnabled } = this.state;
        if (savedSearchId < 1) return;
        const enabled = !emailNotificationEnabled;
        this.setState({ emailNotificationEnabled: enabled });
        this.debouncedNotificationChange(
            savedSearchId,
            enabled,
            requestedEnableStatus => {
                this.setState({
                    emailNotificationEnabled: !requestedEnableStatus
                });
            }
        );
        gtm.trackAction(GA_CATEGORY, enabled ? 'Email removed' : 'Email added');
    }

    async debouncedNotificationChange(savedSearchId, enabled, onError) {
        const responseData = await saveSearchApi.saveEmailNotificationEnabled(
            savedSearchId,
            enabled
        );
        if (!responseData || responseData.error) {
            onError(enabled);
        }
    }

    handleLogin = () => {
        const { queryParams, openSignIn } = this.props;
        const query = {
            ...queryParams,
            saveSearch: 'true'
        };
        const redirectUrl = encodeURIComponent(
            `${window.location.pathname}${toSearchString(query)}`
        );
        openSignIn(redirectUrl);
    };

    async saveSearch() {
        const {
            isLoggedIn,
            searchName,
            queryParams,
            categoryId,
            isSavedSearchEmailEnabled
        } = this.props;
        if (!isLoggedIn) {
            this.handleLogin();
            return;
        }
        this.setState({
            showSavedTooltip: true,
            showHintTooltip: false,
            emailNotificationEnabled: isSavedSearchEmailEnabled,
            inProgress: true
        });

        this.debouncedSaveSearch(
            searchName,
            queryParams,
            categoryId,
            savedSearchId => {
                this.setState({ savedSearchId, inProgress: false });
            },
            () => {
                this.setState({ showSavedTooltip: false, inProgress: false });
            }
        );

        gtm.trackAction('Saved Search', 'Saved');
    }

    async debouncedSaveSearch(
        savedSearchName,
        queryParams,
        categoryId,
        onSuccess,
        onError
    ) {
        const responseData = await saveSearchApi.saveSearch(
            savedSearchName,
            categoryId,
            queryParams
        );

        if (!responseData || responseData.error) {
            onError();
        } else {
            const id =
                (responseData.ids && responseData.ids[0]) || responseData.id;
            onSuccess(id);
        }
    }

    async removeSaved(savedSearchId) {
        const { isLoggedIn } = this.props;

        if (!isLoggedIn) {
            this.handleLogin();
            return;
        }

        if (savedSearchId < 1) return;

        const savedTooltipWasVisible = this.state.showSavedTooltip;

        this.setState({
            showSavedTooltip: false,
            inProgress: true
        });

        this.debouncedRemoveSaved(
            savedSearchId,
            () => this.setState({ savedSearchId: 0, inProgress: false }),
            () => {
                this.setState({
                    savedSearchId,
                    showSavedTooltip: savedTooltipWasVisible,
                    inProgress: false
                });
            }
        );

        gtm.trackAction('Saved Search', 'Removed');
    }

    async debouncedRemoveSaved(savedSearchId, onSuccess, onError) {
        const responseData = await saveSearchApi.removeSavedSearch(
            savedSearchId
        );
        if (!responseData || responseData.error) {
            onError();
        } else {
            onSuccess();
        }
    }

    handleSaveSearchClick = () => {
        const { savedSearchId } = this.state;

        if (savedSearchId > 0) {
            this.removeSaved(savedSearchId);
        } else {
            this.saveSearch();
        }
    };

    render() {
        const {
            showSavedTooltip,
            showHintTooltip,
            savedSearchId,
            emailNotificationEnabled,
            inProgress
        } = this.state;
        const {
            searchName,
            isSavedSearchEmailEnabled,
            t,
            isMobile
        } = this.props;
        const isSaved =
            (savedSearchId > 0 && !inProgress) ||
            (savedSearchId === 0 && inProgress);
        const shouldShowSavedTooltip = showSavedTooltip && isSaved;
        if (!searchName) return null;
        return (
            <div
                className={`tooltip_container tooltip-toggle ${
                    showSavedTooltip || showHintTooltip
                        ? 'tooltip-toggle-show'
                        : ''
                }`}>
                <button
                    type="button"
                    className="btn btn-primary"
                    data-test-save-search-button={inProgress}
                    onClick={() => this.handleSaveSearchClick()}>
                    {isSaved ? (
                        <Fragment>
                            <BookmarkFillIcon className="icon icon-before" />
                            {t('searchResults_saveSearch_saved')}
                        </Fragment>
                    ) : (
                        <Fragment>
                            <BookmarkIcon className="icon icon-before" />
                            {t('searchResults_saveSearch_save')}
                        </Fragment>
                    )}
                </button>
                {shouldShowSavedTooltip && (
                    <span
                        className={`tooltip ${!isMobile &&
                            'tooltip-southwest'}`}>
                        <div className="tooltip_html">
                            <InfoIcon className="tooltip_icon icon" />
                            <span className="tooltip_content">
                                <span>
                                    {t(
                                        'searchResults_saveSearch_savedTooltip',
                                        {
                                            searchName
                                        }
                                    )}
                                </span>
                                {isSavedSearchEmailEnabled ? (
                                    <span className="form-check form-check-sm form-check-white my-1">
                                        <input
                                            type="checkbox"
                                            id="mail-notification"
                                            className="form-check-input"
                                            name="mail-notification"
                                            onChange={() => {
                                                this.toggleEmailNotificationEnabled();
                                            }}
                                            checked={emailNotificationEnabled}
                                        />
                                        <label
                                            htmlFor="mail-notification"
                                            className="form-check-label ml-2 text-nowrap">
                                            {t(
                                                'searchResults_saveSearch_savedTooltipNotify'
                                            )}
                                        </label>
                                    </span>
                                ) : (
                                    <p className="">
                                        {t(
                                            'searchResults_saveSearch_emailDisabledTooltip'
                                        )}
                                    </p>
                                )}
                            </span>
                            <span
                                className="tooltip_close-icon"
                                data-test-save-search-tooltip
                                onClick={() => this.handleCloseSavedTooltip()}>
                                <CloseIcon className="icon icon-sm" />
                            </span>
                        </div>
                    </span>
                )}
                {showHintTooltip && (
                    <span
                        role="tooltip"
                        className={`tooltip ${!isMobile &&
                            'tooltip-southwest'}`}
                        onClick={() => this.handleCloseTooltip()}>
                        <div className="tooltip_html py-1">
                            <BookmarkFillIcon className="tooltip_icon icon icon-branded" />
                            <span
                                className="tooltip_content"
                                style={{ width: '245px' }}>
                                <p className="text-branded mb-1">
                                    {t(
                                        'searchResults_saveSearch_tooltip_title'
                                    )}
                                </p>
                                <span>
                                    {t('searchResults_saveSearch_tooltip')}
                                </span>
                            </span>
                            <span className="tooltip_close-icon">
                                <CloseIcon className="icon icon-sm" />
                            </span>
                        </div>
                    </span>
                )}
            </div>
        );
    }
}

export { SaveSearchButton };
export default compose(withTranslator, withIsMobile)(SaveSearchButton);
